import React, { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Logo } from "./Logo";
import { FontSizeToggle } from "./FontSizeToggle";
import { SECTION_COMPONENTS } from "../Sections";
import { trackSectionView } from "../lib/analytics";

// Presenter view: morphs the scrolling portal into a full-screen slide deck.
// One section per slide, keyboard + click navigation, progress dots, ESC to exit.
export function Presentation({ content, sections, index, setIndex, onExit }) {
  const section = sections[index];

  const go = useCallback(
    (d) => setIndex((i) => Math.min(sections.length - 1, Math.max(0, i + d))),
    [sections.length, setIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        go(1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "Escape") {
        onExit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onExit]);

  useEffect(() => {
    if (section) trackSectionView(section.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const C = section ? SECTION_COMPONENTS[section.id] : null;
  const noop = { onActive: () => {}, registerRef: () => {} };

  return (
    <div data-testid="presentation-overlay" className="fixed inset-0 z-50 bg-ink flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-line px-6 py-3 shrink-0">
        <div className="flex items-center gap-4 min-w-0">
          <Logo imgClass="h-8" />
          {section?.label && (
            <div data-testid="present-section-label" className="flex items-center gap-2 min-w-0">
              <span className="hidden sm:block w-px h-5 bg-line" aria-hidden />
              <span className="font-mono-data text-xs md:text-sm font-black uppercase tracking-[0.22em] text-ftext truncate">
                {section.label}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-5">
          <span data-testid="present-counter" className="font-mono-data text-sm text-sky">
            {String(index + 1).padStart(2, "0")} / {String(sections.length).padStart(2, "0")}
          </span>
          <FontSizeToggle />
          <button
            data-testid="present-exit"
            onClick={onExit}
            aria-label="Exit presentation"
            className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-sky transition-colors"
          >
            <X className="w-4 h-4" /> Exit
          </button>
        </div>
      </div>

      {/* Slide */}
      <div className="relative flex-1 overflow-y-auto overflow-x-hidden">
        <div className="max-w-6xl mx-auto px-2 md:px-8 fade-up" key={section?.id}>
          {C ? <C data={content} common={noop} /> : null}
        </div>

        <button
          data-testid="present-prev"
          onClick={() => go(-1)}
          disabled={index === 0}
          aria-label="Previous slide"
          className="fixed left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 border border-line bg-panel/80 backdrop-blur text-ftext2 hover:text-sky hover:border-sky disabled:opacity-30 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          data-testid="present-next"
          onClick={() => go(1)}
          disabled={index === sections.length - 1}
          aria-label="Next slide"
          className="fixed right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 border border-line bg-panel/80 backdrop-blur text-ftext2 hover:text-sky hover:border-sky disabled:opacity-30 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 border-t border-line py-3 shrink-0">
        {sections.map((s, i) => (
          <button
            key={s.id}
            data-testid={`present-dot-${s.id}`}
            onClick={() => setIndex(i)}
            aria-label={s.label}
            className={`h-1.5 transition-all ${i === index ? "w-6 bg-sky" : "w-1.5 bg-line2 hover:bg-ftext3"}`}
          />
        ))}
      </div>
    </div>
  );
}
